import { createLogger } from "utils/logging";
import { fetchRetry } from "utils/fetch";
import { toggle } from "@otto-ec/global-resources/toggle";
import { captureErrorWithPayload } from "utils/error";

const attributeNameId = "id";
const attributeNameOriginFeature = "feature";
const attributeNameTeam = "team";
const attributeNameVariationId = "variation-id";
const attributeNameProductId = "product-id";
const attributeNameSku = "sku";
const attributeNamePartnerId = "partner-id";

const log = createLogger("tag", "cashback");
const CASHBACK_ELEMENT_NAME = "ft9-cashback-v1";

const ERROR_KEY = "ft9-cashback-not-ok";

/*                                                                                                   */
/*                                                                              */
const MAX_CLEANUP_PERIOD_IN_MILLIS = 20 * 24 * 60 * 60 * 1000;

export class CashbackElement extends HTMLElement {
	async connectedCallback() {
		await this.callBackend();
	}

	private async callBackend() {
		this.replaceChildren(); /*                       */

		const partnerId = this.getAttribute(attributeNamePartnerId);
		if (partnerId !== "1000000" && partnerId !== "999999999") {
			/*                                                                             */
			/*                                                                                     */
			/*                        */
			return;
		}
		const sku = this.getAttribute(attributeNameSku);
		if (!sku) {
			log.error("No sku provided, cannot load cashback.");
			captureErrorWithPayload("No sku provided", "tag", "cashback", ERROR_KEY);
			return;
		}

		const urlSearchParams = this.getSearchParams();
		log.info("Calling tag for %s with %s", this.id || "id-not-set", urlSearchParams.toString());

		try {
			const response = await fetchRetry({
				url: `/benefit-tag/cashback?${urlSearchParams}`,
				namespace: "cashback",
				fetchOptions: {
					headers: {
						Accept: "text/html;charset=UTF-8",
						"X-Requested-With": "XMLHttpRequest",
					},
					priority: "high",
				},
				retryOptions: {
					limit: 2,
					delay: 50,
				},
			});
			if (!response.ok) {
				log.error("tag cashback endpoint returns a non ok response!", response.status);

				const labels = {
					url_search_params: urlSearchParams.toString(),
					tries: 1,
				};

				captureErrorWithPayload("callBackend error", "tag", "cashback", ERROR_KEY, response.status, labels);
				return;
			}
			this.innerHTML = await response.text();
			this.initCashback();
		} catch (error) {
			log.error("tag cashback request returns an error!", error);

			const labels = {
				url_search_params: urlSearchParams.toString(),
				tries: 3,
			};
			captureErrorWithPayload("callBackend error", "tag", "cashback", ERROR_KEY, error, labels);
		}
	}

	private getSearchParams() {
		const urlSearchParams = new URLSearchParams({});

		function addToSearchParam(paramName: string, value: string | null) {
			if (value != null) {
				urlSearchParams.append(paramName, value);
			}
		}

		addToSearchParam("originFeature", this.getAttribute(attributeNameOriginFeature));
		addToSearchParam("team", this.getAttribute(attributeNameTeam));
		addToSearchParam("sku", this.getAttribute(attributeNameSku));
		addToSearchParam("partnerId", this.getAttribute(attributeNamePartnerId));
		return urlSearchParams;
	}

	private initCashback() {
		const cashback = this.firstElementChild;
		if (!cashback || !(cashback instanceof HTMLElement)) {
			return;
		}

		if (cashback.dataset.expirationDate) {
			this.triggerReload(cashback.dataset.expirationDate);
		}
	}

	triggerReload(expirationDate?: string): void {
		if (expirationDate) {
			const date = new Date(expirationDate);

			const delayInMillis: number = Math.min(Math.max(date.getTime() - new Date().getTime(), 1), MAX_CLEANUP_PERIOD_IN_MILLIS);
			window.setTimeout(async () => {
				await this.callBackend();
			}, delayInMillis);
		} else {
			void this.callBackend();
		}
	}
}

if (customElements && customElements.get(CASHBACK_ELEMENT_NAME) === undefined && !toggle.get("ftnine_emergency_custom_element_disable_cashback", false)) {
	customElements.define(CASHBACK_ELEMENT_NAME, CashbackElement);
}
